import React, { useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  Chip,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  ArrowBack as BackIcon,
  DescriptionOutlined as DocIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { getServiceBySlug, getRelatedServices } from '../data/services';
import Seo, { SITE_URL } from '../components/Seo';
import { trackEvent, trackCta } from '../utils/analytics';
import ShareButtons from '../components/ShareButtons';
import NotFound from './NotFound';

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1 }}>
    {children}
  </Typography>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) {
      trackEvent('view_service', { service: service.slug, service_name: service.title });
    }
  }, [service]);

  if (!service) return <NotFound />;

  const related = getRelatedServices(slug);

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    name: service.title,
    description: service.intro,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: 'Worldwide',
    url: `${SITE_URL}/services/${service.slug}`,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${service.title} — scope`,
      itemListElement: service.included.map((item) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: item },
      })),
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
      { '@type': 'ListItem', position: 3, name: service.title, item: `${SITE_URL}/services/${service.slug}` },
    ],
  };

  return (
    <Box>
      <Seo
        title={service.title}
        description={service.intro}
        path={`/services/${service.slug}`}
        image={`${SITE_URL}${service.image}`}
        structuredData={[serviceSchema, breadcrumbSchema]}
      />
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          color: 'white',
          py: { xs: 7, md: 10 },
          mt: { xs: '-64px', md: '-70px' },
          pt: { xs: '120px', md: '140px' },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${service.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(100deg, rgba(1,38,55,0.94) 0%, rgba(1,62,92,0.85) 55%, rgba(1,86,122,0.55) 100%)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.6)' }} />}
            sx={{ mb: 3, '& a, & p': { color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' } }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              Home
            </Link>
            <Link component={RouterLink} to="/services" underline="hover" color="inherit">
              Services
            </Link>
            <Typography sx={{ color: 'white !important' }}>{service.title}</Typography>
          </Breadcrumbs>

          <Box sx={{ maxWidth: 720 }}>
            <SectionLabel>
              <span style={{ color: '#d9bd7c' }}>Inspection Service</span>
            </SectionLabel>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.25rem' }, mb: 2.5 }}>
              {service.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.9)', mb: 4 }}
            >
              {service.intro}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => {
                  trackCta('Request a Quote', `service_${service.slug}_hero`);
                  navigate('/quote');
                }}
                sx={{ px: 4, py: 1.5 }}
              >
                Request a Quote
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<BackIcon />}
                onClick={() => navigate('/services')}
                sx={{
                  px: 4,
                  py: 1.5,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                All Services
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Overview + What's included */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }}>
          <Grid item xs={12} md={7}>
            <SectionLabel>Overview</SectionLabel>
            <Typography variant="h3" gutterBottom>
              What this service covers
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {service.overview}
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
              What's included
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {service.included.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {item}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(15,39,51,0.08)' }}>
              <ShareButtons
                title={service.title}
                path={`/services/${service.slug}`}
                label="Share this service"
              />
            </Box>
          </Grid>

          {/* Sidebar: deliverables */}
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 4, bgcolor: '#f5f8fa', position: { md: 'sticky' }, top: { md: 90 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <DocIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h5" sx={{ color: 'text.primary' }}>
                  What you receive
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                {service.deliverables.map((item, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckIcon sx={{ color: 'secondary.dark', fontSize: 20, mt: 0.3 }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Typical applications
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {service.applications.map((app, index) => (
                  <Chip key={index} label={app} size="small" variant="outlined" color="primary" />
                ))}
              </Box>

              <Button
                variant="contained"
                fullWidth
                endIcon={<ArrowIcon />}
                onClick={() => {
                  trackCta('Discuss your requirement', `service_${service.slug}_sidebar`);
                  navigate('/quote');
                }}
                sx={{ mt: 3, py: 1.25 }}
              >
                Discuss your requirement
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Related Services */}
      <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Related Services</SectionLabel>
            <Typography variant="h2" gutterBottom>
              You may also need
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {related.map((rel) => (
              <Grid item xs={12} sm={6} md={4} key={rel.slug}>
                <Card
                  onClick={() => navigate(`/services/${rel.slug}`)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 18px 40px rgba(15, 39, 51, 0.12)',
                    },
                  }}
                >
                  <CardMedia component="img" image={rel.image} alt={rel.title} sx={{ height: 170, objectFit: 'cover' }} />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                      {rel.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rel.shortDescription}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main' }}>
                      <Typography variant="button">Learn more</Typography>
                      <ArrowIcon fontSize="small" />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            <Box>
              <Typography variant="h3" gutterBottom sx={{ color: 'white' }}>
                Need {service.title.toLowerCase()}?
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                Tell us about your cargo or vessel and we'll respond with a clear scope and quote.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ServiceDetail;
